/**
 * 标签契约 —— **C4 标签**（C 域 PRD §5.4）
 *
 * 依据：C 域 PRD **§5.4**（标签）· **AC-C4.1 ~ C4.6** · `DR-018` §4（**分家的归属判定只认
 * `accounts.ownerId`；标签不作依据** —— 故标签不带任何"归属人优先"语义）· 架构 §4.2 C 段。
 *
 * ## 🔴 三条口径，各自对应一处"最容易做错"
 *
 * | 口径 | 容易做错的地方 |
 * |---|---|
 * | **系统标签与用户标签同表 + `system` 标记**（`AC-C4.1`） | 直觉做法是"系统标签写死在代码里、用户标签落库"—— 那就成了**两套载体**，而且系统标签无法被 `transaction_tags` 外键引用 |
 * | 🔴 **系统标签禁改名、禁删除**（`AC-C4.2`） | 只在 UI 带锁**不算**：直接 `PATCH /api/tags/:id` 仍能改掉 ⇒ **服务端**必须拦（同 B 域系统分类保护） |
 * | **标签无独立颜色字段**（`AC-C4.5`） | 颜色一旦存在，标签就会长成"第二套分类"，与"分类管统计、标签管场景"的定位直接冲突 |
 *
 * ## 为什么要给标签一个**防御性**长度上限
 *
 * 规格与 C 域 PRD **均未规定**标签名长度（`AC-C4.x` 也没有）。此处按
 * `TransactionPolicy.TagNameMaxLength`（量级参照账本名 30）设上限，**只为避免无界字符串落库**，
 * **不构成产品口径** —— 若将来给出正式值，改常量即可（见该常量的文档注释）。
 */

import { z } from 'zod';
import { TransactionPolicy } from '../constants/index.js';

/**
 * 标签名 —— 去空白、非空、≤ {@link TransactionPolicy.TagNameMaxLength}。
 *
 * ⚠️ **不设全账本唯一约束**：`tags` 表上没有 `(ledgerId, name)` 唯一索引（架构 §4.2 C 段），
 * 且 `L` 域合并（`join.service`）按**名字**复用目标账本已有标签 ⇒ 若加唯一约束，
 * 合并路径会在"目标账本已有同名标签"时撞键。**同名标签由服务端在创建时复用**（返回已存在的那条）。
 */
export const TagNameSchema = z
  .string()
  .trim()
  .min(1, '标签名不得为空')
  .max(TransactionPolicy.TagNameMaxLength, `标签名最长 ${TransactionPolicy.TagNameMaxLength} 字符`);

/** `POST /api/tags` —— 新建用户标签（家庭级共享，成员均可创建 —— §5.4） */
export const CreateTagRequestSchema = z.object({ name: TagNameSchema });
export type CreateTagRequest = z.infer<typeof CreateTagRequestSchema>;

/** `PATCH /api/tags/:id` —— 改名（🔴 系统标签 400；🔴 限**创建者或家庭主理人**，服务端校验） */
export const RenameTagRequestSchema = z.object({ name: TagNameSchema });
export type RenameTagRequest = z.infer<typeof RenameTagRequestSchema>;

/**
 * 标签视图（标签管理页 / 详情抽屉）。
 *
 * `usageCount` 由 `transaction_tags` 的计数得到 —— 它是**计数不是金额聚合**
 * （不受 `no-aggregate-in-apps` 约束），且是"删除前二次确认要显示影响面"（`AC-C4.3`）的必要输入。
 */
export interface TagView {
  id: string;
  name: string;
  /** 🔴 系统标签：UI 带锁、服务端拦截改名与删除 */
  system: boolean;
  /** 创建人；系统标签为 `null` */
  ownerId: string | null;
  createdAt: number;
  /** 当前挂在多少笔交易上 */
  usageCount: number;
}

/** 列表响应（标签总量极小：系统 3 + 用户自建，**不分页**） */
export interface TagListView {
  items: TagView[];
}

/**
 * 系统标签（`DR-010` 待缴 / `DR-016` 工资 / 通用）—— 🔴 **注册建账本时预置**，禁改名禁删除。
 *
 * ⚠️ 取值集**就地定义在本文件**（同类先例：`JoinStrategy`）：它是"注册时预置哪几条"的
 * 实现清单，不是"两端各可能写一份"的枚举 ⇒ 不新开 `enums/` 文件（`AC-P1.15` 要求新增共享枚举
 * 须同时改架构 §4.2 与 `P域PRD` §3，而这里没有那个风险）。
 *
 * ⚠️ 名称为**中文文案**，与 `categories` 的身份层（`CAT.*` id 常量）不同 ——
 * 标签没有跨账本稳定 id 的必要（它不是统计口径的载体，只是场景标记）。
 */
export const SYSTEM_TAG_SEED = [
  { key: 'payable-unpaid', name: '待缴·未付' },
  { key: 'payroll-withheld', name: '工资代扣' },
  { key: 'excluded-from-stats', name: '不计收支' },
] as const;
export type SystemTagKey = (typeof SYSTEM_TAG_SEED)[number]['key'];

/**
 * 🆕 批 `107`（K11 · B 域 PRD §5.4.1「示例数据可选」）—— **示例数据的标记标签名**。
 *
 * ## 为什么用标签做标记，而不是加一列 `is_demo`
 *
 * 本批是**零 schema** 批次（业务表仍 43 / 迁移仍 17）。而"这批数据是示例"必须是
 * **结构化标记**（规格 §22.9 的精神：判据不靠文案、不靠备注前缀）——
 * `transaction_tags` 恰好是一个现成的、结构化的、可批量查出/批量删除的关联表。
 *
 * ## 🔴 为什么它**不进** `SYSTEM_TAG_SEED`
 *
 * 系统标签是**注册时预置**的（每个新账本一建账就有），而示例标签**只在用户主动点
 * 「看示例」时才创建**（`AC-B4.2`：示例数据默认不注入）。放进 `SYSTEM_TAG_SEED` 会让
 * 每个账本的标签列表里都多出一条从来没用过的「示例数据」—— 那是噪音，不是功能。
 * ⇒ 按普通用户标签创建（`POST /api/tags` 对**同名标签复用**，故重复点不会造出第二条）。
 *
 * ## ⚠️ 引用方式 = **按名字**（与系统标签同口径）
 *
 * `tags.id` 是**每账本新建的随机 id**（见 `tag-seeder.ts` 文件头：单列主键 + 账本级表），
 * 所以两处要引用它只能按名字。⇒ 名字是**跨端契约**，改动它等于让已注入的示例数据
 * 失去标记（清不掉），**不要改**。
 */
export const DEMO_DATA_TAG_NAME = '示例数据' as const;
